import React, { useState, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from 'date-fns';
import Container from "../../Container/Container";
import SearchComponent from "../../SearchComponent/SearchComponent";
import TableComponent from "../../TableComponent/TableComponent";
import SettingsDelete from "../SettingsDelete/SettingsDelete";
import CreateOrganization from "./CreateOrganization/CreateOrganization";
import EditOrganization from "./CreateOrganization/EditOrganization";
import NoData from "../../NoData/NoData"
import ToastComponent from "../../ToastComponent";
import { AuthContext } from "../../../AuthContext";
import { getTableColumns } from "../../../util";
import './Organizations.css';

// API & Authentication
import { fetchOrganisations } from "../../../apis/organisations.api";
import usePermissions from "../../../hooks/userPermissions";
import ProjectSettingsPage from "../Projects/CreateProject/ProjectSettingsPage";

function Organizations() {
  const { canOrganizationCreate, canOrganizationUpdate, canOrganizationDelete } = usePermissions();
  const { organisationId, projectId, authData } = useContext(AuthContext);
  const { access_token, token_type } = authData
  const [searchResults, setSearchResults] = useState([])
  const [organisationsRows, setOrganisationsRows] = useState([])
  const [organisationsColumns, setOrganisationsColumns] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [openDeleteOrganization, setOpenDeleteOrganization] = useState(false);
  const [openCreateOrganization, setOpenCreateOrganization] = useState(false);
  const [openEditOrganization, setOpenEditOrganization] = useState(false);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Projects");
  // create rows Data
  const createRowsData = (data) => {
    return data?.map(item => {
      if (!item?.is_deleted) {
        return {
          'Org ID': item?.org_id,
          'Organisation Name': item?.name,
          'Created On': format(item?.created_on, 'MM/dd/yyyy, hh:mm a'),
          'Updated On': format(item?.updated_on, 'MM/dd/yyyy, hh:mm a')
        };
      }
      return null;
    }).filter(item => item !== null);
  };
  // // create createColumns Data 
  const createColumnsData = (rowsData) => {
    return [...getTableColumns(rowsData), {
      "field": "action",
      "header": "Action"
    }];
  };

  useEffect(() => {
    let settime;
    if (isToastOpen) {
      settime = setTimeout(() => {
        setIsToastOpen(false);
      }, 4000)
    }

    return () => clearTimeout(settime);
  }, [isToastOpen])
 
  const { data: organisationsData, isLoading: loadingOrganisations, isFetching, refetch, error } = useQuery({
    queryKey: ['organisations', {
      organisationId: organisationId,
      projectId: projectId,
      access_token: access_token,
      token_type: token_type
    }],
    queryFn: fetchOrganisations,
    refetchOnWindowFocus: false
    }
  )

  // Organization serach functionality
  const handleSearchFunctionality = (event) => {
    let resultArray = [];
    const query = event?.toLowerCase();
    if (!query) {
      resultArray = organisationsData;
      setSearchResults(organisationsData)
    } else {
      resultArray = organisationsData?.filter(
        (data) => {
          return data?.name?.toLowerCase().includes(query.toLowerCase());
        }
      );

      setSearchResults(resultArray);
    }
    const rowsData = createRowsData(resultArray);
    const columnsData = createColumnsData(rowsData);
    setOrganisationsRows(rowsData);
    setOrganisationsColumns(columnsData);
  }


  useEffect(() => {
    if (!isToastOpen) {
      refetch();
    }
  }, [isToastOpen, refetch]);

  useEffect(() => {
    if (organisationsData) {
      setSearchResults(organisationsData);
      const rowsData = createRowsData(organisationsData);
      const columnsData = createColumnsData(rowsData);
      setOrganisationsRows(rowsData);
      setOrganisationsColumns(columnsData);
    }
  }, [organisationsData]);


  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, height: '130vh !important' }}
        open={loadingOrganisations || isFetching || loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container>
        <div className="organizationsContainer">
          <div className="organizationsHeader">
            <h2>Your Organizations</h2>
          </div>
          <SearchComponent
            placeholder="Search Organizations"
            // onSearch={(query) => console.log(query)}
            CreateButtonTitle="Create Organization"
            setOpenCreateSettings={setOpenCreateOrganization}
            canCreate={canOrganizationCreate}
            onSearch={handleSearchFunctionality}
          />
          {searchResults?.length > 0 && organisationsColumns.length > 0 && organisationsRows.length > 0 ? (
            <TableComponent
              columns={organisationsColumns}
              rows={organisationsRows}
              setSelected={setSelectedOrg}
              setOpenEditSettings={setOpenEditOrganization}
              setOpenDeleteSettings={setOpenDeleteOrganization}
              canUpdate={canOrganizationUpdate}
              canDelete={canOrganizationDelete}
            />
          ) : (
            <NoData main={'Organizations'} />
          )}


        </div>
        {openCreateOrganization && (
          <CreateOrganization
            openCreateOrganization={openCreateOrganization}
            setOpenCreateOrganization={setOpenCreateOrganization}
            setIsToastOpen={setIsToastOpen}
            setToastMessage={setToastMessage}
            setLoading={setLoading}
            setErrorCode={setErrorCode}
          />
        )}
        {openEditOrganization && (
          <EditOrganization
            openEditOrganization={openEditOrganization}
            setOpenEditOrganization={setOpenEditOrganization}
            selectedOrg={selectedOrg}
            setIsToastOpen={setIsToastOpen}
            setToastMessage={setToastMessage}
            setLoading={setLoading}
            setErrorCode={setErrorCode}
          />
        )}
        {openDeleteOrganization && (
          <SettingsDelete
            openDeleteSettings={openDeleteOrganization}
            setOpenDeleteSettings={setOpenDeleteOrganization}
            title={'Organization'}
            mainTitle={selectedOrg["Organisation Name"] || 'Organization'}
            selected={selectedOrg}
            setIsToastOpen={setIsToastOpen}
            setToastMessage={setToastMessage}
            setLoading={setLoading}
            setErrorCode={setErrorCode}
          />
        )}
       
        {isToastOpen && (
          <ToastComponent
            isToastOpen={isToastOpen}
            setIsToastOpen={setIsToastOpen}
            message={toastMessage}
            errorCode={errorCode}
          />
        )}
      </Container>
    </>
  );
}

export default Organizations;